// Routes where the site-wide bottom bar stays hidden. The bar that fills this slot changed
// on 2026-10-20: the single-vendor "Shop BHG Labs" store bar (StickyStoreBar) was unmounted,
// and StickyBottomCTA's vendor-neutral CTA (quiz / Compare Prices) now takes the mobile slot
// too. FloatingAIChat reads this too, so its chat button sits above the bar on every route
// where a bar shows.
const HIDDEN_PREFIXES = [
  "/blog", // articles keep the bottom slot clear
  "/admin", // internal tooling
  "/embed", // renders inside iframes on other sites
  "/checkout", // no competing CTA during a transaction
  "/account",
  "/compound", // compound pages render their own bottom bar
  "/coach",
  // Dosing pages: no store placement sits beside doses or dosing tools.
  "/protocol",
  "/tools/dosage",
  "/tools/intranasal",
  "/guides/dosage",
  "/guides/dosing-cheat-sheet",
  "/guides/peptide-cheat-sheet-download",
  // Gated Phase 3 research guide: it carries titration doses, so no store bar sits over it.
  "/guides/retatrutide-phase-3-card",

  "/tools/quiz",
  "/tools/reconstitution",
  "/tools/protocol-tracker",
  "/tools/halflife",
  "/tools/nootropic-stack-builder",
  "/nootropic",
];

// /stack/<slug> serves the dosed supplement stacks and the research benches, which publish no doses. Only the benches
// keep the bar, so a stack added later stays hidden until someone checks it.
const RESEARCH_BENCH_SLUGS = new Set([
  "ampa-racetam-study-set",
  "dopamine-pathway-bench",
  "nasal-peptide-bench",
  "adenosine-bench",
  "mitochondrial-bench",
  "tissue-repair-bench",
]);

/** Blog articles, dosage guides and tools suppress store promotions through the same route rule. */
export function isNoStoreRoute(pathname: string): boolean {
  return pathname === "/blog" || pathname.startsWith("/blog/") || pathname === "/tools" || pathname.startsWith("/tools/") || pathname.startsWith("/guides/dosage/");
}

export function storeBarHiddenOn(pathname: string): boolean {
  if (isNoStoreRoute(pathname)) return true;
  if (HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return true;
  // Coach profiles and their protocols render their own bottom bar (CoachProfile.tsx), and BHGChrome drops the site
  // chrome on vendor profiles.
  if (pathname.startsWith("/coaches/") || pathname.startsWith("/vendors/")) return true;
  const stack = pathname.match(/^\/stack\/([^/]+)\/?$/);
  return !!stack && !RESEARCH_BENCH_SLUGS.has(stack[1]);
}

/**
 * Does the site-wide bottom CTA (StickyBottomCTA) own the MOBILE bottom slot on this route?
 *
 * It did not until 2026-10-20: the bar was desktop-only because the single-vendor
 * StickyStoreBar held the mobile slot. That bar is unmounted now, so the CTA comes back
 * wherever the rules above allow a site-wide bar — but never on compound pages, which
 * render their own fixed bottom bar (CompoundPage.tsx) and would have their tap target
 * eaten by a second one (the 2026-07-01 bug).
 */
export function bottomCtaTakesMobileSlot(pathname: string): boolean {
  if (storeBarHiddenOn(pathname)) return false;
  // /compare has its own sticky "Filter and sort" bar on a phone since 2026-10-24 (ComparePage.tsx); with the navbar
  // and a bottom bar as well, a phone showed three bars while scrolling. From 768px the filters sit beside the
  // results, so the bottom bar comes back there.
  if (pathname === "/compare") return false;
  return !pathname.startsWith("/compound/");
}
